import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { homeForRole } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { toast } from "sonner";

const HERO_URL = "/hero-truck.jpg";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accountOptions, setAccountOptions] = useState(null);  // null | array
  const { login } = useAuth();
  const nav = useNavigate();

  const _completeLogin = (user) => {
    toast.success(`Welcome, ${user.email}`);
    nav(homeForRole(user.role), { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result && result.multipleAccounts) {
        setAccountOptions(result.accounts);
      } else {
        _completeLogin(result);
      }
    } catch (err) {
      toast.error(formatError(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const onPickAccount = async (tenantSlug) => {
    setSubmitting(true);
    try {
      const result = await login(email, password, tenantSlug);
      if (result && result.multipleAccounts) {
        setAccountOptions(result.accounts);   // still ambiguous — shouldn't happen
      } else {
        _completeLogin(result);
      }
    } catch (err) {
      toast.error(formatError(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-100 relative">
      {/* Mobile-only hero background */}
      <div className="lg:hidden absolute inset-0 -z-0">
        <img src={HERO_URL} alt="" aria-hidden="true"
             className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/85 via-blue-900/70 to-blue-950/85" />
      </div>

      <div className="relative z-10 flex flex-col justify-between p-6 sm:p-10 lg:p-12 lg:bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-700 grid place-items-center rounded shadow-lg">
            <span className="font-black text-white text-xl">T</span>
          </div>
          <div>
            <div className="font-black text-lg tracking-tight text-white lg:text-slate-900">TruckStop SaaS</div>
            <div className="label-overline text-blue-200 lg:text-slate-500">Operations Console</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0 mt-8 lg:mt-0
                        bg-white/92 lg:bg-transparent backdrop-blur-md lg:backdrop-blur-none
                        border border-white/40 lg:border-0
                        rounded-xl lg:rounded-none shadow-2xl lg:shadow-none
                        p-6 sm:p-8 lg:p-0">
          <div className="label-overline mb-2 text-blue-700">Sign in</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            Enter the console.
          </h1>
          <p className="mt-3 text-slate-600 text-sm">
            One login for the platform, truck stops, and their customers.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div>
              <Label htmlFor="email" className="label-overline">Email</Label>
              <Input id="email" data-testid="login-email" type="email"
                     autoComplete="email" required
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     className="rounded mt-2 h-11 font-mono bg-white"
                     placeholder="you@company.co.za" />
            </div>
            <div>
              <Label htmlFor="password" className="label-overline">Password</Label>
              <Input id="password" data-testid="login-password" type="password"
                     autoComplete="current-password" required
                     value={password} onChange={(e) => setPassword(e.target.value)}
                     className="rounded mt-2 h-11 font-mono bg-white" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={submitting}
                    className="w-full h-11 rounded bg-blue-700 hover:bg-blue-800 text-white font-semibold tracking-wide">
              {submitting ? "Signing in…" : "Sign in →"}
            </Button>
            <div className="text-right">
              <Link to="/forgot-password" data-testid="login-forgot"
                    className="text-xs text-blue-700 hover:underline font-semibold">
                Forgot password?
              </Link>
            </div>
          </form>

          {accountOptions && accountOptions.length > 0 && (
            <div className="mt-6 border-t pt-5" data-testid="login-account-picker">
              <div className="label-overline text-blue-700 mb-2">Choose your account</div>
              <p className="text-sm text-slate-600 mb-3">
                This email is registered at more than one truck stop. Pick which
                one you want to sign in to:
              </p>
              <div className="space-y-2">
                {accountOptions.map((a) => (
                  <button
                    key={`${a.tenant_slug}-${a.role}`}
                    type="button"
                    data-testid={`login-account-option-${a.tenant_slug}`}
                    disabled={submitting}
                    onClick={() => onPickAccount(a.tenant_slug)}
                    className="w-full text-left rounded border border-slate-200 hover:border-blue-500 hover:bg-blue-50/50 transition p-3 flex items-center justify-between disabled:opacity-50"
                  >
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">
                        {a.business_name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {a.portal === "customer"
                          ? "Customer portal"
                          : a.portal === "platform"
                            ? "Platform admin"
                            : "Truck stop staff"}{" "}
                        · <span className="font-mono">{a.role}</span>
                      </div>
                    </div>
                    <span className="text-blue-700 font-semibold text-sm">→</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                data-testid="login-account-picker-cancel"
                onClick={() => setAccountOptions(null)}
                className="mt-3 text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Cancel — try a different email
              </button>
            </div>
          )}
        </div>

        <div className="label-overline text-blue-200 lg:text-slate-400 mt-8">
          © {new Date().getFullYear()} TruckStop SaaS · South Africa
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={HERO_URL} alt="Trucks at a truck stop"
             className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-blue-900/60 to-slate-900/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-end">
          <div className="label-overline text-blue-200">Multi-tenant Truck Stop Operations</div>
          <div className="text-white text-4xl font-black leading-tight tracking-tight mt-3 max-w-md">
            New truck entry.
            <br/>Open orders.
            <br/><span className="text-blue-300">Nothing missed.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
